// Turns a failed `fetch` Response into an Error carrying a message that is
// safe to show a user. The authorisation server returns OAuth-style error
// bodies ({ error, error_description }); when present we surface those. When
// the body is empty or unparseable — e.g. a bare 404 from a missing route, or
// an HTML error page from a proxy — we fall back to a friendly, status-based
// message rather than leaking "HTTP 404" to the screen.

const FRIENDLY_BY_STATUS: Record<number, string> = {
    400: 'Something was wrong with this request. Please start over from the application that sent you here.',
    401: 'Your session has expired. Please sign in again.',
    403: 'You do not have access to complete this request.',
    404: 'This request could not be completed because the service is unavailable. Please try again later, or contact support if the problem persists.',
    408: 'The request timed out. Please try again.',
    429: 'Too many requests. Please wait a moment and try again.',
    500: 'Something went wrong on our side. Please try again later.',
    502: 'The service is temporarily unavailable. Please try again later.',
    503: 'The service is temporarily unavailable. Please try again later.',
    504: 'The service took too long to respond. Please try again later.',
}

function friendlyForStatus(status: number): string {
    if (FRIENDLY_BY_STATUS[status]) return FRIENDLY_BY_STATUS[status]
    if (status >= 500) return FRIENDLY_BY_STATUS[500]!
    if (status >= 400) return FRIENDLY_BY_STATUS[400]!
    return 'Something went wrong. Please try again.'
}

// Reads a server-provided error_description from a JSON body, if any.
// Tolerates non-JSON responses (HTML error pages, empty bodies).
async function readErrorDescription(response: Response): Promise<string | null> {
    try {
        const body = await response.json()
        const description = body?.error_description ?? body?.message
        return typeof description === 'string' && description.trim() ? description : null
    } catch {
        return null
    }
}

// Builds a user-friendly Error from a failed response. `context` is a short
// developer-facing hint (e.g. the endpoint) appended only in dev builds so the
// console still pinpoints the failing call without showing it to users.
export async function httpError(response: Response, context?: string): Promise<Error> {
    const description = await readErrorDescription(response)
    const message = description ?? friendlyForStatus(response.status)
    if (import.meta.env.DEV && context) {
        return new Error(`${message} (${context} → HTTP ${response.status})`)
    }
    return new Error(message)
}
